import { useCallback, useState } from 'react';

import {
    EXPANDED_COLLAPSE_PX,
    EXPAND_DRAG_PX,
    PEEK_DISMISS_PX,
    VELOCITY_OVERRIDE_PX_PER_MS
} from './constants';
import type { HandleDragEnd, SheetState, UseSheetSnapReturn } from './types';

const resolveFromPeek = (dy: number, velocity: number, isFast: boolean): SheetState => {
    if (dy < -EXPAND_DRAG_PX || (isFast && velocity < 0)) {
        return 'full';
    }

    if (dy > PEEK_DISMISS_PX || (isFast && velocity > 0)) {
        return 'hidden';
    }

    return 'peek';
};

const resolveFromFull = (dy: number, velocity: number, isFast: boolean): SheetState => {
    if (dy > EXPANDED_COLLAPSE_PX || (isFast && velocity > 0)) {
        return 'peek';
    }

    if (dy < -EXPAND_DRAG_PX || (isFast && velocity < 0)) {
        return 'full';
    }

    return 'full';
};

const useSheetSnap = (): UseSheetSnapReturn => {
    const [state, setState] = useState<SheetState>('prompt');

    const showContent = useCallback(() => setState('peek'), []);
    const hidePrompt = useCallback(() => setState('hidden'), []);
    const expand = useCallback(() => setState('full'), []);
    const collapse = useCallback(() => setState('peek'), []);
    const dismiss = useCallback(() => setState('hidden'), []);

    const handleDragEnd: HandleDragEnd = useCallback((dy, velocity) => {
        const isFast = Math.abs(velocity) >= VELOCITY_OVERRIDE_PX_PER_MS;

        setState(previous => {
            if (previous === 'peek') {
                return resolveFromPeek(dy, velocity, isFast);
            }

            if (previous === 'full') {
                return resolveFromFull(dy, velocity, isFast);
            }

            return previous;
        });
    }, []);

    return { collapse, dismiss, expand, handleDragEnd, hidePrompt, showContent, state };
};

export default useSheetSnap;
